/**
 * Session Engine v3 — canonical item-input modal.
 *
 * Four sources; availability is gated by context. Platform items are first
 * (the default). Pull tab shows named connector options (Jira, Linear, etc.).
 */

import { useMemo, useState } from 'react';
import { X, Upload, Link2, PencilLine, Boxes, AlertCircle, ExternalLink, Library } from 'lucide-react';
import {
  ITEM_SOURCE_META,
  resolveItemInputCapabilities,
  cannotCoexistConflicts,
} from '../engineLogic';
import type { AddItemsResult } from '../store';
import type { ContextMode, ExclusivityGroup, ItemInputSource, PoolState, StepMode } from '../types';

const SOURCE_ICON: Record<ItemInputSource, React.ComponentType<{ size?: number; className?: string }>> = {
  platform: Boxes,
  pull: Link2,
  type: PencilLine,
  import: Upload,
  collection: Library,
};

// Platform first — the canonical default source when workspace-connected
const SOURCE_ORDER: ItemInputSource[] = ['platform', 'collection', 'pull', 'type', 'import'];

export interface CollectionWithItems {
  id: string;
  name: string;
  description?: string;
  itemIds: string[];
}

// Named connector options for the "pull" tab
const PULL_CONNECTORS = [
  { id: 'jira',    label: 'Jira',          icon: '🟦', available: false },
  { id: 'linear',  label: 'Linear',        icon: '🔵', available: false },
  { id: 'github',  label: 'GitHub Issues', icon: '⚫', available: false },
  { id: 'monday',  label: 'Monday.com',    icon: '🟠', available: false },
  { id: 'asana',   label: 'Asana',         icon: '🔴', available: false },
  { id: 'miro',    label: 'Miro',          icon: '🟡', available: false },
  { id: 'notion',  label: 'Notion',        icon: '⬜', available: false },
  { id: 'trello',  label: 'Trello',        icon: '🔷', available: false },
  { id: 'azure',   label: 'Azure DevOps',  icon: '🟪', available: false },
  { id: 'csv',     label: 'CSV / Sheets',  icon: '📊', available: true  },
];

export interface PlatformItem {
  id: string;
  title: string;
}

interface Props {
  contextMode: ContextMode;
  poolState: PoolState;
  platformItems: PlatformItem[];
  collections: CollectionWithItems[];
  existingItemIds: string[];
  existingTitles: string[];
  exclusivity: ExclusivityGroup[];
  stepMode?: StepMode;
  isFacilitator?: boolean;
  onClose: () => void;
  onAdd: (rows: { title: string; source: ItemInputSource; id?: string }[]) => AddItemsResult;
  onRemoveItem?: (id: string) => void;
}

interface PendingConflict {
  candidate: { id?: string; title: string };
  conflictingItemIds: string[];
}

export function ItemInputModal({
  contextMode,
  poolState,
  platformItems,
  collections,
  existingItemIds,
  existingTitles,
  exclusivity,
  stepMode,
  isFacilitator = true,
  onClose,
  onAdd,
  onRemoveItem,
}: Props) {
  const caps = useMemo(
    () => resolveItemInputCapabilities({ contextMode, poolState, stepMode, isFacilitator }),
    [contextMode, poolState, stepMode, isFacilitator],
  );
  const firstAvailable = SOURCE_ORDER.find((s) => caps[s].available) ?? 'type';
  const [active, setActive] = useState<ItemInputSource>(firstAvailable);

  const [typed, setTyped] = useState('');
  const [pasted, setPasted] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [pendingRows, setPendingRows] = useState<Array<{ title: string; source: ItemInputSource; id?: string }>>([]);
  const [conflicts, setConflicts] = useState<PendingConflict[]>([]);

  const existingTitleSet = useMemo(
    () => new Set(existingTitles.map((t) => t.toLowerCase())),
    [existingTitles],
  );

  const filteredPlatformItems = useMemo(() => {
    if (!search.trim()) return platformItems;
    const q = search.toLowerCase();
    return platformItems.filter((p) => p.title.toLowerCase().includes(q));
  }, [platformItems, search]);

  const titleById = useMemo(() => {
    const map = new Map<string, string>();
    platformItems.forEach((item) => map.set(item.id, item.title));
    return map;
  }, [platformItems]);

  // What a collection would add: its items that are offered here (so not
  // retired) and not already in the pool — the same rows commitCollection adds.
  const addableCollectionIds = (col: CollectionWithItems) =>
    col.itemIds.filter((id) => titleById.has(id) && !existingItemIds.includes(id));
  // A collection whose items are all retired is not "all in pool".
  const collectionOffersAny = (col: CollectionWithItems) => col.itemIds.some((id) => titleById.has(id));

  function findConflicts(rows: Array<{ title: string; source: ItemInputSource; id?: string }>): PendingConflict[] {
    const acceptedIds = [...existingItemIds];
    const found: PendingConflict[] = [];
    for (const row of rows) {
      if (!row.id) continue;
      const conflictingItemIds = cannotCoexistConflicts(exclusivity, acceptedIds, row.id);
      if (conflictingItemIds.length > 0) {
        found.push({ candidate: { id: row.id, title: row.title }, conflictingItemIds });
        continue;
      }
      acceptedIds.push(row.id);
    }
    return found;
  }

  function commitRows(rows: Array<{ title: string; source: ItemInputSource; id?: string }>) {
    const found = findConflicts(rows);
    if (found.length > 0) {
      setPendingRows(rows);
      setConflicts(found);
      return;
    }
    const result = onAdd(rows);
    if (result.conflicts.length > 0) {
      setPendingRows(rows);
      setConflicts(result.conflicts);
      return;
    }
    onClose();
  }

  function removeConflictsAndAdd() {
    const removableIds = new Set(conflicts.flatMap((c) => c.conflictingItemIds).filter((id) => existingItemIds.includes(id)));
    if (removableIds.size === 0 || !onRemoveItem) return;
    removableIds.forEach((id) => onRemoveItem(id));
    const result = onAdd(pendingRows);
    if (result.conflicts.length > 0) {
      setConflicts(result.conflicts);
      return;
    }
    onClose();
  }

  function commitText(source: ItemInputSource, text: string) {
    const rows = text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .filter((t) => !existingTitleSet.has(t.toLowerCase()))
      .map((title) => ({ title, source }));
    if (rows.length) commitRows(rows);
    else onClose();
  }

  function commitPlatform() {
    const rows = platformItems
      .filter((p) => selected.has(p.id))
      .map((p) => ({ id: p.id, title: p.title, source: 'platform' as ItemInputSource }));
    if (rows.length) commitRows(rows);
    else onClose();
  }

  function commitCollection() {
    const col = collections.find((c) => c.id === selectedCollectionId);
    if (!col) return;
    const existingSet = new Set(existingItemIds);
    const rows = platformItems
      .filter((p) => col.itemIds.includes(p.id) && !existingSet.has(p.id))
      .map((p) => ({ id: p.id, title: p.title, source: 'collection' as ItemInputSource }));
    if (rows.length) commitRows(rows);
    else onClose();
  }

  const selectedCollection = collections.find((c) => c.id === selectedCollectionId) ?? null;
  const collectionAddCount = selectedCollection ? addableCollectionIds(selectedCollection).length : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-[var(--rule)] dark:border-[var(--rule)] px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Add items</h3>
            <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">Choose where the items come from. Options depend on context.</p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1.5 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)] transition"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        {/* Source tabs */}
        <div className="grid grid-cols-5 gap-1.5 px-5 pt-4">
          {SOURCE_ORDER.map((source) => {
            const Icon = SOURCE_ICON[source];
            const cap = caps[source];
            const isActive = active === source;
            return (
              <button
                key={source}
                disabled={!cap.available}
                onClick={() => cap.available && setActive(source)}
                title={cap.reason}
                className={[
                  'flex flex-col items-start gap-1.5  border p-3 text-left transition',
                  isActive
                    ? 'border-slate-blue-500 bg-slate-blue-50 text-slate-blue-700 dark:bg-slate-blue-900/40 dark:text-slate-blue-300'
                    : cap.available
                      ? 'border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--surface-raised)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]'
                      : 'cursor-not-allowed border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] opacity-40',
                ].join(' ')}
              >
                <Icon size={15} className={isActive ? 'text-slate-blue-700 dark:text-slate-blue-400' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'} />
                <span className="text-xs font-medium leading-tight">
                  {ITEM_SOURCE_META[source].label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Unavailable reason */}
        {!caps[active].available && (
          <p className="mx-5 mt-3 flex items-center gap-2 bg-luminous-coral-50 border border-luminous-coral-200 dark:bg-luminous-coral-900/30 dark:border-luminous-coral-800/50 px-3 py-2 text-xs text-luminous-coral-800 dark:text-luminous-coral-300">
            <AlertCircle size={13} /> {caps[active].reason}
          </p>
        )}

        {conflicts.length > 0 && (
          <div className="mx-5 mt-3 border border-luminous-coral-200 bg-luminous-coral-50 dark:border-luminous-coral-800/60 dark:bg-luminous-coral-900/20 px-3 py-3 text-sm text-luminous-coral-800 dark:text-luminous-coral-200">
            <div className="flex items-start gap-2">
              <AlertCircle size={15} className="mt-0.5 shrink-0 text-luminous-coral-700 dark:text-luminous-coral-400" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-luminous-coral-800 dark:text-luminous-coral-200">Cannot add conflicting items</p>
                <ul className="mt-2 space-y-1 text-xs text-luminous-coral-700 dark:text-luminous-coral-300">
                  {conflicts.map((conflict) => (
                    <li key={conflict.candidate.id ?? conflict.candidate.title}>
                      <span className="font-medium">{conflict.candidate.title}</span> conflicts with{' '}
                      {conflict.conflictingItemIds.map((id) => titleById.get(id) ?? id).join(', ')}.
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setConflicts([]); setPendingRows([]); }}
                    className="rounded border border-luminous-coral-300 dark:border-luminous-coral-700/50 px-2.5 py-1 text-xs font-medium text-luminous-coral-800 dark:text-luminous-coral-200 hover:bg-luminous-coral-100 dark:hover:bg-luminous-coral-900/40"
                  >
                    Cancel
                  </button>
                  {onRemoveItem && conflicts.every((c) => c.conflictingItemIds.every((id) => existingItemIds.includes(id))) && (
                    <button
                      type="button"
                      onClick={removeConflictsAndAdd}
                      className="rounded bg-luminous-coral-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-luminous-coral-600"
                    >
                      Remove other and add
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab content */}
        <div className="px-5 py-4">
          <p className="mb-3 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">{ITEM_SOURCE_META[active].blurb}</p>

          {/* Type it in */}
          {active === 'type' && (
            <>
              <textarea
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                rows={6}
                placeholder={'One item per line…\nLaunch mobile app\nMigrate billing system'}
                className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-3 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:border-slate-blue-500 focus:outline-none focus:ring-1 focus:ring-slate-blue-500/50"
              />
              <ModalActions onCancel={onClose} onConfirm={() => commitText('type', typed)} disabled={!typed.trim()} confirmLabel={`Add ${typed.trim().split('\n').filter(Boolean).length || ''} items`.trim()} />
            </>
          )}

          {/* Import (CSV paste) */}
          {active === 'import' && (
            <>
              <textarea
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={6}
                placeholder="Paste CSV or one item per line…"
                className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-3 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:border-slate-blue-500 focus:outline-none focus:ring-1 focus:ring-slate-blue-500/50"
              />
              <ModalActions onCancel={onClose} onConfirm={() => commitText('import', pasted)} disabled={!pasted.trim()} />
            </>
          )}

          {/* Pull from source — connector grid */}
          {active === 'pull' && (
            <>
              <div className="mb-4 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {PULL_CONNECTORS.map((c) => (
                  <div
                    key={c.id}
                    className={[
                      'flex items-center gap-2  border px-3 py-2',
                      c.available
                        ? 'border-slate-blue-400 bg-slate-blue-50 text-slate-blue-800 dark:border-slate-blue-600 dark:bg-slate-blue-900/30 dark:text-slate-blue-200 cursor-pointer hover:bg-slate-blue-100 dark:hover:bg-slate-blue-900/60 transition'
                        : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-[var(--text-ter)] dark:text-[var(--text-ter)] cursor-not-allowed',
                    ].join(' ')}
                  >
                    <span className="text-sm leading-none">{c.icon}</span>
                    <span className="flex-1 text-xs font-medium">{c.label}</span>
                    {!c.available && (
                      <span className="text-[9px] uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">Soon</span>
                    )}
                    {c.available && <ExternalLink size={10} className="text-slate-blue-700 dark:text-slate-blue-400" />}
                  </div>
                ))}
              </div>
              <p className="mb-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">No connectors live yet — paste items as a temporary workaround:</p>
              <p className="mb-2 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">From another session — soon.</p>
              <textarea
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={3}
                placeholder="Paste items from your source, one per line…"
                className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-3 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:border-slate-blue-500 focus:outline-none focus:ring-1 focus:ring-slate-blue-500/50"
              />
              <ModalActions onCancel={onClose} onConfirm={() => commitText('pull', pasted)} disabled={!pasted.trim()} />
            </>
          )}

          {/* Collection — pick one, all its items get added */}
          {active === 'collection' && (
            <>
              {collections.length === 0 ? (
                <p className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] px-3 py-6 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  No collections found in this workspace.
                </p>
              ) : (
                <div className="max-h-64 space-y-1.5 overflow-y-auto">
                  {collections.map((col) => {
                    const newCount = addableCollectionIds(col).length;
                    const isSelected = selectedCollectionId === col.id;
                    return (
                      <button
                        key={col.id}
                        type="button"
                        onClick={() => setSelectedCollectionId(isSelected ? null : col.id)}
                        className={[
                          'w-full text-left  border px-3 py-2.5 transition',
                          isSelected
                            ? 'border-slate-blue-500 bg-slate-blue-50 text-[var(--text-pri)] dark:border-slate-blue-600 dark:bg-slate-blue-900/40 dark:text-[var(--text-pri)]'
                            : 'border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]',
                        ].join(' ')}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium leading-snug">{col.name}</span>
                          <span className={`shrink-0 text-xs tabular-nums ${newCount === 0 ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-sec)] dark:text-[var(--text-sec)]'}`}>
                            {newCount === 0
                              ? collectionOffersAny(col) ? 'all in pool' : 'no items to add'
                              : `${newCount} item${newCount !== 1 ? 's' : ''}`}
                          </span>
                        </div>
                        {col.description ? (
                          <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] line-clamp-1">{col.description}</p>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
              <ModalActions
                onCancel={onClose}
                onConfirm={commitCollection}
                disabled={selectedCollection === null || collectionAddCount === 0}
                confirmLabel={
                  selectedCollection
                    ? collectionAddCount === 0
                      ? collectionOffersAny(selectedCollection) ? 'All items already in pool' : 'No items to add'
                      : `Add ${collectionAddCount} item${collectionAddCount !== 1 ? 's' : ''}`
                    : 'Add items'
                }
              />
            </>
          )}

          {/* Platform items — checklist with search */}
          {active === 'platform' && (
            <>
              {platformItems.length === 0 ? (
                <p className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] px-3 py-6 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  No platform items found in this workspace.
                </p>
              ) : (
                <>
                  {platformItems.length >= 8 && (
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search items…"
                      className="mb-2 w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:border-slate-blue-500 focus:outline-none"
                    />
                  )}
                  <div className="max-h-64 space-y-0.5 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] p-1">
                    {filteredPlatformItems.map((p) => {
                      const already = existingItemIds.includes(p.id);
                      const conflicts = cannotCoexistConflicts(exclusivity, existingItemIds, p.id);
                      const blocked = conflicts.length > 0;
                      const checked = selected.has(p.id);
                      return (
                        <label
                          key={p.id}
                          className={[
                            'flex items-center gap-2.5  px-2.5 py-2 text-sm cursor-pointer',
                            checked ? 'bg-slate-blue-100 text-[var(--text-pri)] dark:bg-slate-blue-900/50 dark:text-[var(--text-pri)]' : 'text-[var(--text-sec)] dark:text-[var(--text-sec)]',
                            already || blocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]',
                          ].join(' ')}
                          title={blocked ? 'Cannot coexist with an item already in the pool' : undefined}
                        >
                          <input
                            type="checkbox"
                            disabled={already || blocked}
                            checked={checked}
                            onChange={(e) => {
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(p.id);
                                else next.delete(p.id);
                                return next;
                              });
                            }}
                            className="accent-slate-blue-500 h-3.5 w-3.5 shrink-0"
                          />
                          <span className="flex-1 leading-snug">{p.title}</span>
                          {already && <span className="text-[10px] uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">in pool</span>}
                          {blocked && <span className="text-[10px] uppercase tracking-wide text-luminous-coral-700 dark:text-luminous-coral-500">conflict</span>}
                        </label>
                      );
                    })}
                    {filteredPlatformItems.length === 0 && (
                      <p className="py-4 text-center text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">No items match "{search}"</p>
                    )}
                  </div>
                  {selected.size > 0 && (
                    <p className="mt-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">{selected.size} selected</p>
                  )}
                </>
              )}
              <ModalActions
                onCancel={onClose}
                onConfirm={commitPlatform}
                disabled={selected.size === 0}
                confirmLabel={selected.size > 0 ? `Add ${selected.size} item${selected.size !== 1 ? 's' : ''}` : 'Add items'}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalActions({
  onCancel,
  onConfirm,
  disabled,
  confirmLabel = 'Add items',
}: {
  onCancel: () => void;
  onConfirm: () => void;
  disabled: boolean;
  confirmLabel?: string;
}) {
  return (
    <div className="mt-4 flex justify-end gap-2">
      <button
        onClick={onCancel}
        className="border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        disabled={disabled}
        className="bg-slate-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition"
      >
        {confirmLabel}
      </button>
    </div>
  );
}
